/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface ActiveCardUnitOfferingProps {
  id: string;
  cardId: string;
  unitId: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class ActiveCardUnitOfferingRecord extends Record<ActiveCardUnitOfferingProps>({
  id: null as any,
  cardId: null as any,
  unitId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    cardId: string;
    unitId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): ActiveCardUnitOfferingRecord {
    if (json == null) {
      throw new Error('ActiveCardUnitOfferingRecord.fromJS: parameter cannot be null');
    }
    return new ActiveCardUnitOfferingRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'ActiveCardUnitOfferingJson.id',
      ),
      cardId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'cardId', '00000000-0000-0000-0000-000000000000'),
        'ActiveCardUnitOfferingJson.cardId',
      ),
      unitId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'unitId', '00000000-0000-0000-0000-000000000000'),
        'ActiveCardUnitOfferingJson.unitId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'ActiveCardUnitOfferingJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    cardId: string;
    unitId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [ActiveCardUnitOfferingRecord, Error[]] {
    if (json == null) {
      throw new Error('ActiveCardUnitOfferingRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new ActiveCardUnitOfferingRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'ActiveCardUnitOfferingJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      cardId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'cardId', '00000000-0000-0000-0000-000000000000'),
            'ActiveCardUnitOfferingJson.cardId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      unitId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'unitId', '00000000-0000-0000-0000-000000000000'),
            'ActiveCardUnitOfferingJson.unitId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'ActiveCardUnitOfferingJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: ActiveCardUnitOfferingProps) {
    super(props);
    if (props == null) {
      throw new Error('ActiveCardUnitOfferingRecord constructor parameter cannot be null');
    }
  }
}

export abstract class ActiveCardUnitOfferingViewModelBase implements ActiveCardUnitOfferingProps {
  protected readonly _props: ActiveCardUnitOfferingProps;
  private _json?: ActiveCardUnitOfferingProps;

  get id(): string {
    return this._props.id;
  }
  get cardId(): string {
    return this._props.cardId;
  }
  get unitId(): string {
    return this._props.unitId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: ActiveCardUnitOfferingProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        cardId: this._props.cardId,
        unitId: this._props.unitId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllActiveCardUnitOfferingEntities = (state: AppState) =>
  state.entityStores.activeCardUnitOffering.entities;

export const selectActiveCardUnitOfferingById = (id: string) => (state: AppState) =>
  state.entityStores.activeCardUnitOffering.entities.get(id);

export const selectActiveCardUnitOfferingMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.activeCardUnitOffering.meta.get(id);

export const selectActiveCardUnitOfferingAndMetadataById = (id: string) =>
  createSelector(
    selectActiveCardUnitOfferingById(id),
    selectActiveCardUnitOfferingMetadataById(id),
    (activeCardUnitOffering, meta) => ({ activeCardUnitOffering, meta }),
  );

export function selectActiveCardUnitOfferingEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllActiveCardUnitOfferingEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, ActiveCardUnitOfferingRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectActiveCardUnitOfferingEntitiesByCardId(cardId: string) {
  return createSelector(selectAllActiveCardUnitOfferingEntities, (entities) =>
    entities.filter((e) => e.cardId === cardId).valueSeq(),
  );
}

export function selectActiveCardUnitOfferingEntitiesByUnitId(unitId: string) {
  return createSelector(selectAllActiveCardUnitOfferingEntities, (entities) =>
    entities.filter((e) => e.unitId === unitId).valueSeq(),
  );
}

export function selectActiveCardUnitOfferingEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllActiveCardUnitOfferingEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
